import { useMemo } from 'react';

import {
  IFeedbackInitOptions,
  FeedbackAuthenticationType,
  FeedbackAgeGroup,
  FeedbackPolicyValue,
  FeedbackUiType,
  FeedbackScreenshotSourceType,
  FeedbackScreenshotInfoImageFormat,
  FeedbackHostPlatformType,
} from '@ms/inapp-feedback-loader';
import { webLightTheme } from '@fluentui/react-components';
import { getFeedbackConfig } from '@config/feedbackConfig';
import { getActiveAccount } from '@/services/token/tokenService';

export const useInAppFeedbackHelper = (onDismiss: () => void) => {
  const account = getActiveAccount();
  const tenantId = account?.tenantId;
  // OCV expects AAD user ids to carry the `a:` prefix.
  const userId = account?.localAccountId ? `a:${account.localAccountId}` : undefined;

  const defaultFeedbackOptions = useMemo<IFeedbackInitOptions>(
    () => {
      const feedbackConfig = getFeedbackConfig();

      return {
        appId: feedbackConfig.appId,
        clientName: feedbackConfig.clientName,
        isProduction: feedbackConfig.isProduction,
        authenticationType: FeedbackAuthenticationType.AAD,
        ageGroup: FeedbackAgeGroup.Adult,
        tenantId: tenantId,
        userId: userId,
        callbackFunctions: {
          onDismiss: onDismiss,
        },
        feedbackConfig: {
          email: '',
          feedbackUiType: FeedbackUiType.SidePane,
          isDisplayed: false,
          hostPlatform: FeedbackHostPlatformType.Web,
          isEmailCollectionEnabled: true,
          invokeOnDismissOnEsc: true,
          isFeedbackForumEnabled: false,
          isMyFeedbackEnabled: false,
          isScreenshotEnabled: true,
          isScreenRecordingEnabled: false,
          isThankYouPageDisabled: false,
          isFileUploadEnabled: false,
          screenshotInfo: {
            providedScreenshotType: FeedbackScreenshotSourceType.AutoCaptureWithHtml2Canvas,
            screenshotImageFormat: FeedbackScreenshotInfoImageFormat.PNG,
          },
          complianceChecks: {
            connectedExperiences: FeedbackPolicyValue.Enabled,
            policyAllowContact: FeedbackPolicyValue.Enabled,
            policyAllowContent: FeedbackPolicyValue.Enabled,
            policyAllowFeedback: FeedbackPolicyValue.Enabled,
            policyAllowScreenshot: FeedbackPolicyValue.Enabled,
            policyAllowSurvey: FeedbackPolicyValue.Disabled,
            policyScreenshotDefault: FeedbackPolicyValue.Enabled,
          },
        },
        themeOptions: {
          isFluentV9: true,
          v9Theme: webLightTheme,
        },
      };
    },
    [onDismiss, tenantId, userId]
  );

  return {
    defaultFeedbackOptions,
  };
};

export default useInAppFeedbackHelper;